import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api';
import { isPlatformAdmin, useAuth } from '@/shared/auth';
import { companyKeys } from '@/features/team/queries';
import { userKeys } from '../queries';

/**
 * So viele Protokolleinträge zeigt die Akte. Mehr wäre keine Zeitleiste mehr, sondern eine
 * zweite Ausgabe des Änderungsprotokolls — und die gibt es unter „System" bereits.
 */
const ACTIVITY_PAGE_SIZE = 25;

/**
 * Alles, was die Benutzerakte zeigt. Der Access-Read-Vertrag liefert die serverautoritativ
 * aufgeloeste Sicht; die Oberfläche kombiniert keine Einzelquellen selbst.
 */
export function useUserDetail(userId: string) {
  const api = useApi();
  // Spiegelt die Server-Policy `RequirePlatformAdmin` des Protokoll-Endpunkts. Ohne diese
  // Vorprüfung liefe die Akte für jeden Workspace-Administrator in einen 403 und die Karte
  // zeigte dauerhaft einen Fehler, den niemand beheben kann.
  const canReadAuditLog = isPlatformAdmin(useAuth().user);

  const user = useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => api.users.getById(userId),
  });

  const access = useQuery({
    queryKey: userKeys.access(userId),
    queryFn: () => api.users.getAccess(userId),
  });

  const companyId = access.data?.user.companyId ?? user.data?.companyId ?? null;
  const company = useQuery({
    queryKey: companyKeys.detail(companyId ?? 'none'),
    queryFn: () => api.companies.getById(companyId!),
    enabled: companyId !== null,
  });

  const audit = useQuery({
    queryKey: ['system', 'audit', 'by-user', userId],
    queryFn: () => api.system.auditLogs({ userId, page: 1, pageSize: ACTIVITY_PAGE_SIZE }),
    enabled: canReadAuditLog,
  });

  return {
    user,
    access,
    company,
    activity: {
      canReadAuditLog,
      entries: audit.data?.items ?? [],
      loading: audit.isLoading && canReadAuditLog,
      error: audit.isError,
      // Ehrlich statt still: es gibt mehr Einträge, als diese Karte zeigt.
      truncated: Number(audit.data?.total ?? 0) > ACTIVITY_PAGE_SIZE,
    },
  };
}
